import {
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, BoxProps, Flex } from "@chakra-ui/react";
import { captureMessage } from "@sentry/nextjs";
import CollapseToggleButton, {
  CollapseToggleIcon,
} from "components/Shared/CollapseToggleButton";

const DEFAULT_MAX_HEIGHT = 125;

const FOOTER_HEIGHT = "1.5rem";

type ContainerProps = Omit<BoxProps, "maxH" | "maxHeight"> & {
  contentRef?: RefObject<HTMLDivElement>;
  footerContent?: ReactNode;
  isExpanded?: boolean;
  isNotNeeded?: boolean;
  maxHeight?: number | string;
};

export function MaxHeightContainer({
  children,
  contentRef,
  footerContent,
  isExpanded,
  isNotNeeded,
  maxHeight = DEFAULT_MAX_HEIGHT,
  ...rest
}: ContainerProps) {
  return (
    <Box
      position="relative"
      data-not-needed={isNotNeeded || undefined}
      data-expanded={isExpanded || isNotNeeded || undefined}
    >
      <Box
        style={{ maxHeight }}
        overflow="hidden"
        transition="max-height 500ms ease, mask-image 500ms ease"
        sx={{
          "div:not([data-not-needed]) > &": { paddingBottom: FOOTER_HEIGHT },
          "div:not([data-expanded]) > &": {
            maskImage:
              `linear-gradient(to bottom, black calc(100% - ${FOOTER_HEIGHT} - 3em), ` +
              `transparent calc(100% - ${FOOTER_HEIGHT} / 2))`,
          },
        }}
        {...rest}
      >
        <Box ref={contentRef}>{children}</Box>
      </Box>

      {!isNotNeeded && (
        <Flex
          position="absolute"
          bottom={0}
          width="100%"
          height={FOOTER_HEIGHT}
          alignItems="center"
          justifyContent="center"
        >
          {footerContent}
        </Flex>
      )}
    </Box>
  );
}

type Props = Omit<BoxProps, "maxH" | "maxHeight"> & {
  collapseButtonLabel?: ReactNode;
  expandButtonLabel?: ReactNode;
  maxHeight?: number;
};

export default function MaxHeightExpander({
  collapseButtonLabel = "Collapse",
  expandButtonLabel = "Show More",
  maxHeight = DEFAULT_MAX_HEIGHT,
  ...rest
}: Props) {
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const innerRef = useRef<HTMLDivElement>(null);

  const resizeObserver = useMemo(() => {
    if (typeof ResizeObserver === "undefined") return;
    return new ResizeObserver((entries) => {
      if (!entries.length) return;
      if (entries.length > 1) {
        captureMessage("More than one entry in ResizeObserver");
      }
      const entry = entries[0];
      setContentHeight(entry.contentRect.height);
    });
  }, []);

  useEffect(() => {
    if (resizeObserver && innerRef.current) {
      resizeObserver.observe(innerRef.current);
    }
    return () => {
      resizeObserver?.disconnect();
    };
  }, [resizeObserver]);

  const isNotNeeded = contentHeight === null || contentHeight < maxHeight;

  return (
    <MaxHeightContainer
      contentRef={innerRef}
      isExpanded={isExpanded}
      isNotNeeded={isNotNeeded}
      maxHeight={
        isExpanded
          ? `calc(${contentHeight ?? 10000}px + ${FOOTER_HEIGHT})`
          : maxHeight
      }
      footerContent={
        !isNotNeeded && (
          <CollapseToggleButton
            size="xs"
            variant="link"
            fontWeight="bold"
            color="gray.500"
            isExpanded={isExpanded}
            onExpandedChange={setIsExpanded}
            _hover={{ textDecoration: "none", color: "lightPink" }}
          >
            <CollapseToggleIcon
              collapsedOffsetDegrees={-90}
              expandedOffsetDegrees={180}
              mr={1}
            />
            {isExpanded ? collapseButtonLabel : expandButtonLabel}
          </CollapseToggleButton>
        )
      }
      {...rest}
    />
  );
}
